/**
 * Utility functions for handling motion preferences and animations
 */

/**
 * Safely checks if the user prefers reduced motion
 * @returns boolean indicating if reduced motion is preferred
 */
export const prefersReducedMotion = (): boolean => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return false;
  }

  // Check if matchMedia is available
  if (!window.matchMedia) {
    return false;
  }

  try {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    return mediaQuery.matches;
  } catch (error) {
    // If there's any error accessing matchMedia, default to false
    return false;
  }
};

/**
 * Gets the appropriate transition string based on motion preferences
 * @param normalTransition - The normal transition string
 * @param reducedTransition - The reduced motion transition string
 * @returns The appropriate transition string
 */
export const getMotionSafeTransition = (
  normalTransition: string,
  reducedTransition: string = 'opacity 0.2s ease-out'
): string => {
  return prefersReducedMotion() ? reducedTransition : normalTransition;
};

/**
 * Gets motion-safe animation duration
 * @param normalDuration - Normal animation duration in milliseconds
 * @param reducedDuration - Reduced motion duration in milliseconds
 * @returns The appropriate duration
 */
export const getMotionSafeDuration = (
  normalDuration: number,
  reducedDuration: number = 50
): number => {
  return prefersReducedMotion() ? reducedDuration : normalDuration;
};
